// src/antdviews/Header.tsx

// 头部导航组件 - Ant Design 重构版

import axios from 'axios'
import React from 'react'
import localStorage from 'localStorage'
import { withTranslation, WithTranslation } from 'react-i18next'
import {
    Layout,
    Dropdown,
    Menu,
    Space,
    Avatar,
    Typography,
    Select,
    Button,
} from 'antd'
import {
    UserOutlined,
    LogoutOutlined,
    GlobalOutlined,
    MenuOutlined,
    DownOutlined,
    SettingOutlined,
    BgColorsOutlined, // 使用 BgColorsOutlined 替代 PaletteOutlined
} from '@ant-design/icons'
import { ROLE } from '../../services/Macro'
import { AuthApi, ROOT } from '../../services/Api'
import { encrypUtil } from '../../utils/EncrypUtil'
import { Page } from '../../services/Page'
import { themes, applyTheme, loadTheme } from '../../theme/themeConfig'

const { Header: AntHeader } = Layout
const { Text } = Typography
const { Option } = Select

interface HeaderProps extends WithTranslation {
    loginName?: string
    changeLang: () => void
    i18n: any
    trans: (key: string) => string
}

interface HeaderState {
    options: { value: string; label: string }[]
    lang: string
    themeKey: string
    visible: boolean
}

class Header extends React.Component<HeaderProps, HeaderState> {

    state: HeaderState = {
        options: [
            { value: 'zh_CN', label: '简体中文' },
            { value: 'en_US', label: 'English' },
        ],
        lang: localStorage.getItem('lang') || 'zh_CN',
        themeKey: loadTheme(),
        visible: false,
    }

    handleLogout = () => {
        // 通知服务器移除token
        this.notifyLogout()
        localStorage.removeItem('token')
    }

    notifyLogout = async () => {
        const DEBUG = true
        const TAG = `Header.notifyLogout() - `

        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const requestData = {
            token: token,
            lang: lang.split('_')[0],
        }
        DEBUG && console.log(TAG, `requestData:\n`, requestData)
        const params = await encrypUtil.encodeRequestRsa(requestData)
        DEBUG && console.log(TAG, `params:\n`, params)
        const api = AuthApi.LOGOUT
        const response = await axios.post(api, params)
        DEBUG && console.log(TAG, `账号登出:\n`, response.data.data)
        // 跳转到登录页面
        window.location.href = `${ROOT}/${Page.login}`
    }

    changeLanguage = (lang: string) => {
        const { i18n } = this.props
        i18n.changeLanguage(lang)
    }

    /** 多语言切换 */
    handleLang = (value: string) => {
        const lang = value
        console.log('lang:', lang)
        localStorage.setItem('lang', lang)
        this.setState({ lang })
        this.changeLanguage(lang)
        this.props.changeLang()
    }

    /** 切换主题 */
    handleThemeChange = (themeKey: string) => {
        this.setState({ themeKey })
        applyTheme(themeKey)
    }

    toggleSidebar = () => {
        console.log(`开关手机浏览器侧边栏`)

        // 在 html 上增减一个 sidebar-left-opened 样式
        const html = document.getElementsByTagName('html')[0]
        console.log(html)
        if (html.classList.contains('sidebar-left-opened')) {
            html.classList.remove('sidebar-left-opened')
        } else {
            html.classList.add('sidebar-left-opened')
        }
    }

    getRoleName = (role: number) => {
        const t = this.props.trans
        switch (role) {
            case ROLE.SUPER:
                return t('admin') || '管理员'
            // case ROLE.AGENT:
            //     return t('agent')
            default:
                return t('normalUser') || '普通用户'
        }
    }

    render() {
        const t = this.props.trans
        const { loginName = 'User' } = this.props
        const { options, lang, themeKey } = this.state

        const role = Number(localStorage.getItem('role')) || ROLE.SUPER
        const roleStr = this.getRoleName(role)

        // 用户下拉菜单
        const userMenu = (
            <Menu>
                <Menu.Item
                    key="logout"
                    icon={<LogoutOutlined />}
                    onClick={this.handleLogout}
                    style={{ color: '#ff4d4f' }}
                >
                    {t('logout') || '退出登录'}
                </Menu.Item>
            </Menu>
        )

        return (
            <AntHeader
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--header-bg)',
                    padding: '0 24px',
                    boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
                    height: 64,
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                }}
            >
                {/* 左侧：Logo + 汉堡菜单（移动端） */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {/* 移动端侧边栏切换按钮 */}
                    <Button
                        type="text"
                        icon={<MenuOutlined style={{ fontSize: 18 }} />}
                        onClick={this.toggleSidebar}
                        style={{
                            display: 'none',
                            marginRight: 12,
                            fontSize: 18,
                        }}
                        className="visible-xs"
                    />
                    <a href="./" className="logo">
                        <img
                            src="./assets/images/logo.microfb.png"
                            height="60"
                            alt="Porto Admin"
                            style={{ height: 60 }}
                        />
                    </a>
                </div>

                {/* 右侧：主题切换 + 语言切换 + 用户信息 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {/* 主题切换 */}
                    {this.renderThemeSwitch()}

                    {/* 语言切换 */}
                    <Select
                        value={lang}
                        onChange={this.handleLang}
                        style={{ width: 120 }}
                        suffixIcon={<GlobalOutlined />}
                        bordered={false}
                    >
                        {options.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Option>
                        ))}
                    </Select>

                    <span className="separator" style={{ color: '#e8e8e8' }}>
                        |
                    </span>

                    {/* 用户下拉 */}
                    <Dropdown overlay={userMenu} placement="bottomRight" trigger={['click']}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: 4,
                                transition: 'background 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--primary-bg)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent'
                            }}
                        >
                            <Avatar
                                size={36}
                                icon={<UserOutlined />}
                                src="./assets/images/!logged-user.jpg"
                                style={{ backgroundColor: 'var(--primary-color)' }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                <Text strong style={{ fontSize: 14, color: 'var(--text-color)' }}>
                                    {loginName}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                    {roleStr}
                                </Text>
                            </div>
                            <DownOutlined style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }} />
                        </div>
                    </Dropdown>
                </div>
            </AntHeader>
        )
    }

    renderThemeSwitch() {
        const SHOW_THEME_SWITCH = true;
        const t = this.props.trans
        const { themeKey, lang } = this.state

        // 根据语言设置下拉框宽度
        const getSelectWidth = () => {
            const currentLang = lang || localStorage.getItem('lang') || 'zh_CN'
            // 中文使用 120px，英文及其他使用 160px
            return currentLang === 'zh_CN' || currentLang === 'zh_TW' ? 110 : 160
        }

        // 主题选项
        const themeOptions = themes.map(theme => ({
            value: theme.key,
            label: t(theme.nameKey),
        }))
        if (SHOW_THEME_SWITCH) {
            return (
                <>
                    <Select
                        value={themeKey}
                        onChange={this.handleThemeChange}
                        style={{ width: getSelectWidth() }}  // 动态宽度
                        suffixIcon={<BgColorsOutlined />}
                        bordered={false}
                        placeholder={t('theme') || '主题'}
                        dropdownStyle={{ maxHeight: 300 }}  // 控制下拉菜单最大高度
                        // 或使用 listHeight（antd 4.x+）
                        listHeight={300}  // 单位：像素
                    >
                        {themeOptions.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                                <Space>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            background: themes.find(t => t.key === opt.value)?.colors.primary,
                                            border: '1px solid #d9d9d9',
                                        }}
                                    />
                                    {opt.label}
                                </Space>
                            </Option>
                        ))}
                    </Select>

                    <span className="separator" style={{ color: '#e8e8e8' }}>
                        |
                    </span>
                </>
            );

        }
        else {
            return null;
        }
    }
}

export default withTranslation()(Header)